import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  CheckCircle2,
  Car,
  ShieldCheck,
  Banknote,
  Clock,
  ArrowRight,
  Sparkles,
  FileSignature,
  KeyRound,
  Send,
} from "lucide-react";
import Calculator from "@/components/Calculator";
import HeroSlideshow from "@/components/HeroSlideshow";
import FeaturedVehicles from "@/components/FeaturedVehicles";
import { api } from "@/lib/api";

const ELIGIBILITY = [
  { title: "21 or older", desc: "You must be at least 21 years of age." },
  { title: "Valid Australian licence", desc: "Hold a current Australian driver's licence." },
  { title: "13.5% advance payment", desc: "Required upfront — calculated from the vehicle's drive-away price." },
  { title: "Valid ABN", desc: "Active Australian Business Number required." },
  { title: "Proof of income", desc: "Recent bank statements or payslips." },
  { title: "No credit checks", desc: "We focus on where you're at now — not your past." },
];

const STEPS = [
  { n: "01", t: "Select your car", d: "Simply select your car from a dealership — any brand-new sedan, SUV or ute.", Icon: Car },
  { n: "02", t: "Apply with us", d: "Quick online application. No credit checks — approved within 24 to 48 hours.", Icon: Send },
  { n: "03", t: "Pay 13.5% & sign", d: "Lock in your 1, 2 or 3 year plan and pay the advance payment to get going.", Icon: FileSignature },
  { n: "04", t: "Drive & own", d: "Pay weekly, fortnightly or monthly. Complete the term — the keys are yours.", Icon: KeyRound },
];

export default function Home() {
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#050505]">
      {/* HERO SLIDESHOW */}
      <HeroSlideshow />

      {/* Eligibility & Social Proof strip */}
      <section
        data-testid="section-proof-strip"
        className="relative bg-gradient-to-b from-[#050505] to-[#080808] border-y border-[#D4AF37]/10"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          <ProofStat icon={<ShieldCheck size={16} />} label="No credit checks" sub="ABN-holder focused" />
          <ProofStat icon={<Clock size={16} />} label="24–48 hr approval" sub="Fast turnaround" />
          <ProofStat icon={<Car size={16} />} label="Brand-new vehicles" sub="2026 stock only" />
          <ProofStat icon={<Banknote size={16} />} label="13.5% advance" sub="Transparent terms" />
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-32">
            <div className="label-eyebrow mb-4">02 — Calculator</div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-tight" style={{ fontWeight: 300 }}>
              See what your weekly drive looks like.
            </h2>
            <p className="text-white/60 mt-6 leading-relaxed">
              Adjust the vehicle, deposit and term — we'll show you the indicative repayment.
              The same simple structure used across our fleet.
            </p>
            <div className="mt-8 p-5 border-l-2 border-[#D4AF37] bg-white/[0.02]">
              <p className="text-sm text-white/80 italic font-display text-lg leading-relaxed" style={{ fontWeight: 400 }}>
                "Save $35 every week. Own the car after 3 years."
              </p>
              <p className="text-xs text-white/40 mt-2">— Rent vs. Own example</p>
            </div>
          </div>
          <div className="lg:col-span-8">
            <Calculator />
          </div>
        </div>
      </section>

      {/* FEATURED VEHICLES */}
      <FeaturedVehicles />

      {/* ELIGIBILITY */}
      <section className="py-24 md:py-32 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-7">
              <div className="label-eyebrow mb-4">03 — Eligibility</div>
              <h2 className="font-display text-4xl md:text-5xl text-white" style={{ fontWeight: 300 }}>
                Practical criteria. <span className="italic text-[#D4AF37]">No surprises.</span>
              </h2>
            </div>
            <div className="md:col-span-5 text-white/60">
              We don't run credit checks. We focus on where you're at now, not your past — so the
              path forward is clear.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {ELIGIBILITY.map((e, i) => (
              <div
                key={e.title}
                data-testid={`eligibility-${i}`}
                className="bg-[#0a0a0a] p-8 hover:bg-[#0E0E0E] transition-colors group"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[#D4AF37] font-display text-2xl" style={{ fontWeight: 500 }}>
                    0{i + 1}
                  </span>
                  <div className="h-px flex-1 bg-white/10 group-hover:bg-[#D4AF37]/30 transition-colors" />
                </div>
                <h3 className="text-white text-lg mb-2">{e.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow mb-4">04 — How it works</div>
          <h2 className="font-display text-4xl md:text-5xl text-white mb-14 max-w-3xl" style={{ fontWeight: 300 }}>
            From browsing to ownership — in <span className="italic text-[#D4AF37]">four steps</span>.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <StepCard key={s.n} step={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* VEHICLES */}
      <section className="py-24 md:py-32 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="label-eyebrow mb-4">05 — The Fleet</div>
              <h2 className="font-display text-4xl md:text-5xl text-white max-w-2xl" style={{ fontWeight: 300 }}>
                Brand-new 2026 stock. <br />
                <span className="italic text-[#D4AF37]">Pick what suits the road ahead.</span>
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="vehicles-see-all"
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#D4AF37] transition-colors"
            >
              See all services <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {vehicles.slice(0, 8).map((v) => (
              <article
                key={v.id}
                data-testid={`vehicle-card-${v.id}`}
                className="group border border-white/10 bg-[#0a0a0a] overflow-hidden hover:border-[#D4AF37]/40 transition-all"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#111]">
                  <img
                    src={v.image}
                    alt={`${v.make} ${v.model}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase tracking-[0.18em] text-white/40">
                      {v.type} · {v.year}
                    </span>
                    <Car size={14} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="text-white text-lg leading-tight">
                    {v.make} <span className="text-white/60">{v.model}</span>
                  </h3>
                  <div className="mt-4 pt-4 border-t border-white/5 flex items-baseline justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                        From
                      </div>
                      <div className="font-display text-2xl text-[#D4AF37]" style={{ fontWeight: 400 }}>
                        ${v.weekly_from}
                        <span className="text-xs text-white/50 ml-1">/ wk</span>
                      </div>
                    </div>
                    <Link
                      to="/apply"
                      data-testid={`vehicle-apply-${v.id}`}
                      className="text-xs uppercase tracking-[0.18em] text-white hover:text-[#D4AF37] transition-colors"
                    >
                      Apply →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="label-eyebrow mb-6">Ready when you are</div>
          <h2 className="font-display text-white text-4xl md:text-6xl leading-tight" style={{ fontWeight: 300 }}>
            The next chapter starts <br className="hidden md:block" />
            with the <span className="italic text-[#D4AF37]">right set of keys.</span>
          </h2>
          <p className="text-white/60 mt-6 max-w-xl mx-auto">
            Submit your application in minutes. Our team will be in touch within 24 to 48 hours.
          </p>
          <Link
            to="/apply"
            data-testid="bottom-cta-apply"
            className="inline-flex mt-10 items-center gap-2 bg-[#D4AF37] text-black px-10 py-5 text-sm tracking-wide font-medium hover:bg-[#F6ECCC] transition-colors"
          >
            Begin Your Application <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Trust({ icon, label }) {
  return (
    <div className="flex flex-col items-start gap-2">
      <div className="text-[#D4AF37]">{icon}</div>
      <span className="text-xs text-white/60 leading-snug">{label}</span>
    </div>
  );
}

/* StepCard — scroll-triggered fade-up with staggered delay,
   continuous ping pulse on the big number, animated icon, and bottom rail draw on hover. */
function StepCard({ step, index }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const { Icon } = step;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.25, rootMargin: "0px 0px -10% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-testid={`step-${step.n}`}
      className={`group relative border border-white/10 p-8 bg-gradient-to-b from-white/[0.02] to-transparent overflow-hidden transition-all duration-700 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      } hover:border-[#D4AF37]/50 hover:-translate-y-1`}
      style={{ transitionDelay: visible ? `${index * 140}ms` : "0ms" }}
    >
      {/* Hover-only bottom rail that "draws" from left to right */}
      <span
        aria-hidden="true"
        className="absolute bottom-0 left-0 h-px w-0 bg-[#D4AF37] transition-all duration-500 ease-out group-hover:w-full"
      />

      {/* Top row: big number + animated icon */}
      <div className="flex items-start justify-between mb-6">
        <div className="relative">
          <div
            className="font-display text-5xl text-[#D4AF37]"
            style={{ fontWeight: 300 }}
          >
            {step.n}
          </div>
          {/* Continuous ping ring around the number — alive feel */}
          <span
            aria-hidden="true"
            className="absolute -left-1 top-1 w-2 h-2 rounded-full bg-[#D4AF37] opacity-70 animate-ping"
            style={{ animationDelay: `${index * 0.35}s` }}
          />
          <span
            aria-hidden="true"
            className="absolute -left-1 top-1 w-2 h-2 rounded-full bg-[#D4AF37]"
          />
        </div>

        {Icon && (
          <div className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] transition-transform duration-500 group-hover:rotate-12 group-hover:border-[#D4AF37]">
            <Icon size={18} className="transition-transform duration-500 group-hover:scale-110" />
          </div>
        )}
      </div>

      <h3 className="text-white text-xl mb-3">{step.t}</h3>
      <p className="text-sm text-white/50 leading-relaxed">{step.d}</p>
    </div>
  );
}


function ProofStat({ icon, label, sub }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 flex items-center justify-center border border-[#D4AF37]/30 rounded-full text-[#D4AF37] bg-black/40 backdrop-blur">
        {icon}
      </div>
      <div>
        <div className="text-white text-sm font-medium">{label}</div>
        <div className="text-white/40 text-xs">{sub}</div>
      </div>
    </div>
  );
}
